import { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { ChartBarIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';

const DOMAIN_COLOR = { cognitive:'#3b6ef8', affective:'#10c98e', psychomotor:'#f59e0b' };
const curYear = () => { const y=new Date().getFullYear(); return `${y}-${y+1}`; };

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .pd * { box-sizing:border-box; }
  .pd { font-family:'Sora',sans-serif; color:#1a1d2e; animation:pdFade .4s ease both; }
  @keyframes pdFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .pd-hdr { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .pd-title { font-size:1.35rem; font-weight:700; color:#0f1123; letter-spacing:-.02em; }
  .pd-sub   { font-size:.76rem; color:#9094a8; margin-top:3px; }
  .btn { display:inline-flex; align-items:center; gap:7px; padding:8px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-purple { background:#8b5cf6; color:white; } .btn-purple:hover { background:#7c3aed; }
  .btn-ghost  { background:#f0f2f7; color:#6b7194; } .btn-ghost:hover { background:#e4e7f0; }
  .btn:disabled { opacity:.5; cursor:not-allowed; }
  .filters { display:flex; gap:10px; margin-bottom:1.4rem; flex-wrap:wrap; align-items:flex-end; }
  .fl { display:flex; flex-direction:column; gap:4px; }
  .fl-label { font-size:.67rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .fl-input { font-family:'Sora',sans-serif; font-size:.8rem; padding:8px 12px; border:1.5px solid #e0e4f0; border-radius:9px; background:white; outline:none; min-width:130px; }
  .fl-input:focus { border-color:#8b5cf6; }
  /* Overall card */
  .overall-card { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:1.4rem; margin-bottom:1.4rem; display:flex; align-items:center; gap:20px; flex-wrap:wrap; }
  .overall-ring { position:relative; width:90px; height:90px; flex-shrink:0; }
  .overall-ring-text { position:absolute; inset:0; display:flex; flex-direction:column; align-items:center; justify-content:center; }
  .overall-pct { font-size:1.3rem; font-weight:700; font-family:'JetBrains Mono',monospace; line-height:1; }
  .overall-lbl { font-size:.6rem; font-weight:600; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; margin-top:2px; }
  .overall-info { flex:1; min-width:0; }
  .overall-title { font-size:.96rem; font-weight:700; color:#0f1123; margin-bottom:4px; }
  .overall-meta  { font-size:.74rem; color:#9094a8; }
  .overall-status { display:inline-flex; font-size:.76rem; font-weight:700; padding:4px 12px; border-radius:99px; margin-top:6px; }
  .overall-status.achieved    { background:#ecfdf5; color:#065f46; }
  .overall-status.not-achieved{ background:#fef2f2; color:#991b1b; }
  /* PLO grid */
  .plo-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(340px,1fr)); gap:1rem; margin-bottom:1rem; }
  .plo-card { background:white; border-radius:14px; border:1px solid #e8ebf4; padding:1.2rem; transition:box-shadow .15s; }
  .plo-card:hover { box-shadow:0 4px 16px rgba(0,0,0,.06); }
  .plo-card-top { display:flex; align-items:flex-start; gap:10px; margin-bottom:.8rem; }
  .plo-code-badge { font-family:'JetBrains Mono',monospace; font-size:.76rem; font-weight:700; padding:3px 9px; border-radius:7px; background:#f3f0ff; color:#8b5cf6; flex-shrink:0; white-space:nowrap; }
  .plo-desc { font-size:.78rem; color:#1a1d2e; flex:1; line-height:1.5; }
  .plo-pills { display:flex; gap:5px; flex-wrap:wrap; margin-bottom:.9rem; }
  .pill { display:inline-flex; font-size:.64rem; font-weight:600; padding:2px 8px; border-radius:99px; white-space:nowrap; text-transform:capitalize; }
  .att-bar-wrap { margin-bottom:.8rem; }
  .att-bar-row { display:flex; justify-content:space-between; align-items:center; margin-bottom:4px; }
  .att-bar-label { font-size:.72rem; color:#6b7194; }
  .att-bar-pct { font-family:'JetBrains Mono',monospace; font-size:.82rem; font-weight:700; }
  .att-bar { height:8px; background:#f0f2f8; border-radius:99px; overflow:hidden; }
  .att-bar-fill { height:100%; border-radius:99px; transition:width .6s ease; }
  .status-chip { display:inline-flex; font-size:.68rem; font-weight:700; padding:3px 9px; border-radius:99px; }
  .status-chip.achieved    { background:#ecfdf5; color:#10c98e; }
  .status-chip.not-achieved{ background:#fef2f2; color:#ef4444; }
  .status-chip.no-data     { background:#f0f2f7; color:#9094a8; }
  /* CLO breakdown */
  .clo-breakdown { background:#fafbff; border-radius:10px; border:1px solid #f0f2f8; overflow:hidden; margin-top:8px; }
  .clo-bd-head { padding:8px 12px; font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; cursor:pointer; display:flex; align-items:center; justify-content:space-between; background:#f5f6fb; }
  .clo-bd-row { display:flex; align-items:center; justify-content:space-between; padding:7px 12px; border-top:1px solid #f0f2f8; gap:8px; flex-wrap:wrap; }
  .clo-bd-code { font-family:'JetBrains Mono',monospace; font-size:.7rem; font-weight:700; color:#3b6ef8; background:#eef2ff; padding:2px 7px; border-radius:5px; flex-shrink:0; }
  .clo-bd-desc { font-size:.72rem; color:#1a1d2e; flex:1; }
  .clo-bd-meta { font-size:.66rem; color:#9094a8; }
  .pd-empty { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; background:white; border-radius:16px; border:1px solid #e8ebf4; }
  .pd-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#8b5cf6; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg)} }
  @media (max-width:768px) { .pd-title{font-size:1.1rem;} .filters{flex-direction:column;} .fl-input{width:100%;min-width:unset;} .plo-grid{grid-template-columns:1fr;} }
`;

const Ring = ({ pct=0, size=90, stroke=8, color='#8b5cf6' }) => {
  const r=size/2-stroke/2, circ=2*Math.PI*r, dash=(pct/100)*circ;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{transform:'rotate(-90deg)'}}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#f0f2f8" strokeWidth={stroke}/>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" style={{transition:'stroke-dasharray .6s ease'}}/>
    </svg>
  );
};

export default function PLOAttainmentDashboard({ role='admin' }) {
  const [academicYear, setAcademicYear] = useState(curYear());
  const [data,         setData]         = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [expanded,     setExpanded]     = useState({});

  const attainmentApi = role === 'coordinator' ? '/coordinator/plos/attainment'
                      : role === 'teacher'      ? '/teacher/plos/attainment'
                      : '/admin/plos/attainment';
  const overallColor  = pct => pct >= 80 ? '#10c98e' : pct >= 60 ? '#f59e0b' : '#ef4444';

  const fetchAttainment = async () => {
    if (!academicYear) return toast.error('Enter academic year');
    setLoading(true);
    try {
      const { data: res } = await api.get(`${attainmentApi}?academicYear=${academicYear}`);
      setData(res);
    } catch (err) {
      const msg = err.response?.data?.message;
      if (msg?.includes('OBE is not enabled')) toast.error('OBE is not enabled — enable it in CLO Management → Settings');
      else toast.error(msg || 'Failed to load PLO attainment');
    } finally { setLoading(false); }
  };

  const handleExportCSV = () => {
    if (!data?.attainment?.length) return;
    const rows = data.attainment.map(a => ({
      'PLO Code':      a.plo.code,
      'Description':   a.plo.description,
      'Domain':        a.plo.domain,
      'Threshold %':   a.threshold,
      'CLOs Mapped':   a.cloCount,
      'Attainment %':  a.attainmentPct,
      'Status':        a.status,
    }));
    const headers = Object.keys(rows[0]);
    const csv = [headers.join(','), ...rows.map(r => headers.map(h => `"${r[h]}"`).join(','))].join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type:'text/csv' }));
    a.download = `PLO_Attainment_${academicYear}.csv`;
    a.click();
  };

  return (
    <div className="pd"><style>{css}</style>

      <div className="pd-hdr">
        <div>
          <div className="pd-title">PLO Attainment Dashboard</div>
          <div className="pd-sub">Program-level outcome attainment aggregated from CLO results</div>
        </div>
        {data && <button className="btn btn-ghost" onClick={handleExportCSV}><ArrowDownTrayIcon />Export CSV</button>}
      </div>

      <div className="filters">
        <div className="fl">
          <label className="fl-label">Academic Year *</label>
          <input className="fl-input" value={academicYear} onChange={e=>setAcademicYear(e.target.value)} placeholder="2025-2026" style={{minWidth:130}} />
        </div>
        <button className="btn btn-purple" style={{alignSelf:'flex-end'}} onClick={fetchAttainment} disabled={loading}>
          <ChartBarIcon />{loading?'Calculating…':'View PLO Attainment'}
        </button>
      </div>

      {loading && <div className="pd-loading"><div className="spinner"/>Calculating PLO attainment…</div>}

      {!loading && !data && (
        <div className="pd-empty">
          <ChartBarIcon style={{width:32,height:32,margin:'0 auto 12px',display:'block',opacity:.3}}/>
          Enter an academic year and click View PLO Attainment.
          <div style={{fontSize:'.72rem',marginTop:8,color:'#b0b5cc'}}>PLO attainment is calculated from all CLO results mapped to each PLO for the selected year.</div>
        </div>
      )}

      {!loading && data && (<>
        {/* Overall summary */}
        <div className="overall-card">
          <div className="overall-ring">
            <Ring pct={data.overallAttainment} size={90} stroke={8} color={overallColor(data.overallAttainment)}/>
            <div className="overall-ring-text">
              <div className="overall-pct" style={{color:overallColor(data.overallAttainment)}}>{data.overallAttainment}%</div>
              <div className="overall-lbl">Overall</div>
            </div>
          </div>
          <div className="overall-info">
            <div className="overall-title">Program Learning Outcomes — {academicYear}</div>
            <div className="overall-meta">{data.attainment?.length} PLOs · default threshold: {data.threshold}%</div>
            <div className={`overall-status ${data.overallAttainment >= data.threshold ? 'achieved' : 'not-achieved'}`}>
              {data.overallAttainment >= data.threshold ? '✓ Program attainment achieved' : '✗ Program attainment not achieved'}
            </div>
          </div>
          <div style={{display:'flex',gap:16,flexWrap:'wrap'}}>
            {[
              { label:'Achieved',     val: data.attainment?.filter(a=>a.status==='achieved').length||0,     color:'#10c98e' },
              { label:'Not Achieved', val: data.attainment?.filter(a=>a.status==='not-achieved').length||0, color:'#ef4444' },
              { label:'No Data',      val: data.attainment?.filter(a=>a.status==='no-data').length||0,      color:'#9094a8' },
            ].map(s=>(
              <div key={s.label} style={{textAlign:'center'}}>
                <div style={{fontSize:'1.3rem',fontWeight:700,fontFamily:"'JetBrains Mono',monospace",color:s.color}}>{s.val}</div>
                <div style={{fontSize:'.66rem',color:'#9094a8',fontWeight:600}}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {data.attainment?.length === 0 ? (
          <div className="pd-empty">No PLOs defined yet. Add PLOs in PLO Management and map CLOs to them.</div>
        ) : (
          <div className="plo-grid">
            {data.attainment.map(a => {
              const barColor = a.attainmentPct>=80?'#10c98e':a.attainmentPct>=60?'#f59e0b':'#ef4444';
              const isExp    = !!expanded[a.plo._id];
              return (
                <div key={a.plo._id} className="plo-card">
                  <div className="plo-card-top">
                    <span className="plo-code-badge">{a.plo.code}</span>
                    <span className="plo-desc">{a.plo.description}</span>
                  </div>
                  <div className="plo-pills">
                    <span className="pill" style={{background:`${DOMAIN_COLOR[a.plo.domain]||'#3b6ef8'}18`,color:DOMAIN_COLOR[a.plo.domain]||'#3b6ef8'}}>{a.plo.domain}</span>
                    <span className={`status-chip ${a.status}`}>
                      {a.status==='achieved'?'✓ Achieved':a.status==='not-achieved'?'✗ Not Achieved':'— No Data'}
                    </span>
                    <span style={{fontSize:'.66rem',color:'#9094a8',fontWeight:600,padding:'2px 8px'}}>{a.cloCount} CLO{a.cloCount!==1?'s':''}</span>
                  </div>
                  <div className="att-bar-wrap">
                    <div className="att-bar-row">
                      <span className="att-bar-label">PLO Attainment</span>
                      <span className="att-bar-pct" style={{color:barColor}}>{a.attainmentPct}%</span>
                    </div>
                    <div className="att-bar"><div className="att-bar-fill" style={{width:`${a.attainmentPct}%`,background:barColor}}/></div>
                    <div style={{fontSize:'.67rem',color:'#b0b5cc',marginTop:3}}>Threshold: {a.threshold}% · Average of {a.mappedCLOs?.filter(c=>c.status!=='no-data').length||0} CLO attainment(s)</div>
                  </div>
                  {/* CLO breakdown */}
                  {a.mappedCLOs?.length > 0 && (
                    <div className="clo-breakdown">
                      <div className="clo-bd-head" onClick={()=>setExpanded(p=>({...p,[a.plo._id]:!p[a.plo._id]}))}>
                        Contributing CLOs ({a.mappedCLOs.length}) {isExp?'▲':'▼'}
                      </div>
                      {isExp && a.mappedCLOs.map(clo => (
                        <div key={clo._id} className="clo-bd-row">
                          <span className="clo-bd-code">{clo.code}</span>
                          <div style={{flex:1,minWidth:0}}>
                            <div className="clo-bd-desc">{clo.description}</div>
                            <div className="clo-bd-meta">{clo.className} · {clo.subject}</div>
                          </div>
                          <div style={{textAlign:'right',flexShrink:0}}>
                            <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:'.76rem',fontWeight:700,color:clo.attainmentPct>=60?'#10c98e':'#ef4444'}}>{clo.attainmentPct}%</div>
                            <span className={`status-chip ${clo.status}`} style={{fontSize:'.6rem',padding:'1px 6px'}}>{clo.status==='achieved'?'✓':clo.status==='not-achieved'?'✗':'—'}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </>)}
    </div>
  );
}